import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Briefcase, FileCheck2, TrendingUp, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import SEO from "../components/SEO";
import { toast } from "sonner";

export default function Signup() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [organization, setOrganization] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(name, email, password, undefined, acceptedTerms, organization);
      navigate("/app");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <SEO title="Start Free Trial — hireqix" description="Create your hireqix workspace and get 10 free credits to screen and rank your first candidates." canonicalPath="/signup" />
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-[#0B1220] p-12 text-white lg:flex">
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "26px 26px" }}
        />
        <div className="relative flex items-center justify-between gap-4 animate-in fade-in-0 slide-in-from-left-4 duration-500">
          <Link to="/" className="rounded-xl bg-white px-4 py-3 shadow-lg" data-testid="signup-logo-home-link">
            <Logo className="h-11" />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-1.5 text-xs font-medium text-slate-300 transition-colors duration-150 hover:border-white/30 hover:text-white"
            data-testid="signup-home-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to home
          </Link>
        </div>
        <div className="relative animate-in fade-in-0 slide-in-from-left-4 duration-700">
          <h2 className="font-heading text-3xl font-bold leading-tight tracking-tight">
            Set up your hiring workspace in under a minute.
          </h2>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-400">
            Define your own competency matrix, invite your team, and automatically rank every candidate against it.
          </p>
          <div className="mt-10 space-y-4">
            <Feature icon={Briefcase} text="Unlimited job requisitions per workspace" />
            <Feature icon={FileCheck2} text="Bulk PDF/DOCX resume scoring in minutes" />
            <Feature icon={TrendingUp} text="Instant ranked shortlists with hiring verdicts" />
          </div>
        </div>
        <div className="relative text-xs text-slate-500">© {new Date().getFullYear()} hireqix</div>
      </div>

      <div className="flex w-full items-center justify-center px-6 lg:w-1/2">
        <div className="w-full max-w-sm animate-in fade-in-0 slide-in-from-bottom-2 duration-500">
          <div className="mb-10 flex items-center justify-between lg:hidden">
            <Link to="/" data-testid="signup-home-link-mobile"><Logo className="h-12" /></Link>
            <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-3.5 w-3.5" /> Home
            </Link>
          </div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Create your workspace</h1>
          <p className="mt-1 text-sm text-muted-foreground">Start evaluating resumes in minutes</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground">Full name</label>
              <Input required placeholder="Jane Doe" value={name} onChange={(e) => setName(e.target.value)} data-testid="signup-name-input" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground">Email</label>
              <Input
                type="email"
                required
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="signup-email-input"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Organisation <span className="normal-case tracking-normal text-muted-foreground/70">(optional)</span>
              </label>
              <Input
                placeholder="Acme Hiring Pvt Ltd"
                value={organization}
                onChange={(e) => setOrganization(e.target.value)}
                data-testid="signup-organization-input"
              />
              <p className="mt-1.5 text-xs text-muted-foreground">
                We'll name your workspace after it. Leave blank to use your own name.
              </p>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground">Password</label>
              <Input
                type="password"
                required
                minLength={8}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="signup-password-input"
              />
              <p className="mt-1.5 text-xs text-muted-foreground" data-testid="password-policy-hint">
                At least 8 characters, including a letter and a number.
              </p>
            </div>
            <label className="flex items-start gap-2.5 text-xs leading-relaxed text-muted-foreground">
              <input
                type="checkbox"
                required
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                data-testid="signup-terms-checkbox"
                className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-border accent-primary"
              />
              <span>
                I agree to the{" "}
                <Link to="/terms" target="_blank" className="font-medium text-primary hover:underline">Terms &amp; Conditions</Link>{" "}
                and the{" "}
                <Link to="/privacy" target="_blank" className="font-medium text-primary hover:underline">Privacy Policy</Link>, and I
                consent to my data being processed to provide the service.
              </span>
            </label>
            <Button type="submit" disabled={loading || !acceptedTerms} className="w-full" size="lg" data-testid="signup-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Workspace"}
            </Button>
          </form>
          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="font-medium text-primary hover:underline" data-testid="go-to-login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function Feature({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-3 text-sm text-slate-300">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/5">
        <Icon className="h-4 w-4 text-sky-400" />
      </div>
      {text}
    </div>
  );
}
